import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loading, EmptyState } from "@/components/States";
import { api, formatError } from "@/lib/api";

function ExplanationItem({ e, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [c, setC] = useState(e.content);
  const save = async (body) => {
    try { await api.put(`/admin/explanations/${e.id}`, body); toast.success("Saved"); setEdit(false); onSaved(); } catch (er) { toast.error(formatError(er)); }
  };
  return (
    <div data-testid={`ae-item-${e.id}`} className="rounded-xl border border-line bg-surface p-5">
      <p className="text-xs text-ink-3"><span className="font-semibold capitalize">{e.status}</span> · {e.subject_name} · <span className="capitalize">{e.difficulty}</span> · {new Date(e.created_at).toLocaleString()}</p>
      <h4 className="mt-1 font-bold text-ink">{e.topic}</h4>
      {edit ? (
        <div className="mt-3 flex flex-col gap-2">
          <label className="text-xs font-semibold text-ink-3">Overview</label>
          <Textarea data-testid={`ae-overview-${e.id}`} rows={5} value={c.overview} onChange={(ev) => setC({ ...c, overview: ev.target.value })} />
          <label className="text-xs font-semibold text-ink-3">Summary</label>
          <Textarea rows={3} value={c.summary} onChange={(ev) => setC({ ...c, summary: ev.target.value })} />
          <label className="text-xs font-semibold text-ink-3">Key takeaways (one per line)</label>
          <Textarea rows={4} value={c.key_takeaways.join("\n")} onChange={(ev) => setC({ ...c, key_takeaways: ev.target.value.split("\n") })} />
          <label className="text-xs font-semibold text-ink-3">Uncertainty note</label>
          <Textarea rows={2} value={c.uncertainty_note} onChange={(ev) => setC({ ...c, uncertainty_note: ev.target.value })} />
          <div className="flex gap-2">
            <Button size="sm" data-testid={`ae-save-${e.id}`} onClick={() => save({ content: { ...c, key_takeaways: c.key_takeaways.filter((t) => t.trim()) } })}>Save corrections</Button>
            <Button size="sm" variant="ghost" onClick={() => setEdit(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <p className="mt-2 line-clamp-3 text-sm text-ink-2">{e.content.overview}</p>
      )}
      {!edit && (
        <div className="mt-3 flex flex-wrap gap-2">
          {e.status !== "approved" && <Button size="sm" data-testid={`ae-approve-${e.id}`} className="bg-emerald-700 hover:bg-emerald-800" onClick={() => save({ status: "approved" })}>Approve</Button>}
          {e.status !== "retired" && <Button size="sm" variant="outline" data-testid={`ae-retire-${e.id}`} onClick={() => save({ status: "retired" })}>Retire</Button>}
          <Button size="sm" variant="ghost" data-testid={`ae-edit-${e.id}`} onClick={() => setEdit(true)}>Review & correct</Button>
        </div>
      )}
    </div>
  );
}

export default function AdminContent() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState(null);
  const load = () => api.get(`/admin/explanations${status === "all" ? "" : `?status=${status}`}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="ae-filter-status" className="w-40 capitalize"><SelectValue /></SelectTrigger>
          <SelectContent>{["all", "pending", "approved", "retired"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
        </Select>
        <p className="text-sm text-ink-3">Approved explanations are cached and served to all learners, saving AI cost.</p>
      </div>
      {!items ? <Loading /> : items.length === 0 ? <EmptyState testId="ae-empty" title="Nothing to review" /> : (
        <div className="flex flex-col gap-3">{items.map((e) => <ExplanationItem key={e.id} e={e} onSaved={load} />)}</div>
      )}
    </div>
  );
}
